import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './AlbumManager.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

function AlbumManager({ isOpen, onClose, onAlbumChange, embedded = false }) {
    const [albums, setAlbums] = useState([]);
    const [editingAlbum, setEditingAlbum] = useState(null);
    const [newAlbumName, setNewAlbumName] = useState('');
    const [newAlbumDescription, setNewAlbumDescription] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (isOpen) {
            fetchAlbums();
        }
    }, [isOpen]);

    const fetchAlbums = async () => {
        try {
            const response = await axios.get(`${API_BASE}/albums`, {
                withCredentials: true
            });
            setAlbums(response.data);
        } catch (error) {
            console.error('Error fetching albums:', error);
            setError('Failed to load albums');
        }
    };

    const handleCreateAlbum = async (e) => {
        e.preventDefault();
        if (!newAlbumName.trim()) {
            setError('Album name is required');
            return;
        }

        setLoading(true);
        setError('');

        try {
            await axios.post(`${API_BASE}/albums`, {
                name: newAlbumName,
                description: newAlbumDescription
            }, {
                withCredentials: true
            });

            setNewAlbumName('');
            setNewAlbumDescription('');
            fetchAlbums();
            if (onAlbumChange) onAlbumChange();
        } catch (error) {
            setError(error.response?.data?.error || 'Failed to create album');
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateAlbum = async (albumId, name, description) => {
        setLoading(true);
        setError('');

        try {
            await axios.put(`${API_BASE}/albums/${albumId}`, {
                name,
                description
            }, {
                withCredentials: true
            });

            setEditingAlbum(null);
            fetchAlbums();
            if (onAlbumChange) onAlbumChange();
        } catch (error) {
            setError(error.response?.data?.error || 'Failed to update album');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteAlbum = async (albumId, isDefault) => {
        if (isDefault) {
            setError('Cannot delete default album');
            return;
        }

        if (!window.confirm('Are you sure you want to delete this album? Photos will be moved to the default album.')) {
            return;
        }

        setLoading(true);
        setError('');

        try {
            await axios.delete(`${API_BASE}/albums/${albumId}`, {
                withCredentials: true
            });

            fetchAlbums();
            if (onAlbumChange) onAlbumChange();
        } catch (error) {
            setError(error.response?.data?.error || 'Failed to delete album');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen && !embedded) return null;

    const content = (
        <>
            {error && <div className="error-message">{error}</div>}

            <div className="album-manager-content">
                <div className="create-album-section">
                    <h3>Create New Album</h3>
                    <form onSubmit={handleCreateAlbum}>
                        <input
                            type="text"
                            placeholder="Album name"
                            value={newAlbumName}
                            onChange={(e) => setNewAlbumName(e.target.value)}
                            disabled={loading}
                        />
                        <textarea
                            placeholder="Description (optional)"
                            value={newAlbumDescription}
                            onChange={(e) => setNewAlbumDescription(e.target.value)}
                            disabled={loading}
                        />
                        <button type="submit" disabled={loading}>
                            {loading ? 'Creating...' : 'Create Album'}
                        </button>
                    </form>
                </div>

                <div className="albums-list-section">
                    <h3>Your Albums</h3>
                    <div className="albums-list">
                        {albums.map(album => (
                            <div key={album.id} className={`album-item ${album.is_default ? 'default' : ''}`}>
                                {editingAlbum?.id === album.id ? (
                                    <div className="album-edit-form">
                                        <input
                                            type="text"
                                            value={editingAlbum.name}
                                            onChange={(e) => setEditingAlbum({...editingAlbum, name: e.target.value})}
                                        />
                                        <textarea
                                            value={editingAlbum.description || ''}
                                            onChange={(e) => setEditingAlbum({...editingAlbum, description: e.target.value})}
                                        />
                                        <div className="album-edit-actions">
                                            <button onClick={() => handleUpdateAlbum(album.id, editingAlbum.name, editingAlbum.description)}>
                                                Save
                                            </button>
                                            <button onClick={() => setEditingAlbum(null)}>Cancel</button>
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        <div className="album-info">
                                            <h4>{album.name} {album.is_default && <span className="default-badge">Default</span>}</h4>
                                            {album.description && <p>{album.description}</p>}
                                        </div>
                                        <div className="album-actions">
                                            {!album.is_default && (
                                                <>
                                                    <button onClick={() => setEditingAlbum(album)}>Edit</button>
                                                    <button onClick={() => handleDeleteAlbum(album.id, album.is_default)} className="delete-btn">
                                                        Delete
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </>
    );

    if (embedded) {
        return content;
    }

    return (
        <div className="album-manager-overlay" onClick={onClose}>
            <div className="album-manager-panel" onClick={(e) => e.stopPropagation()}>
                <div className="album-manager-header">
                    <h2>Manage Albums</h2>
                    <button className="close-button" onClick={onClose}>&times;</button>
                </div>
                {content}
            </div>
        </div>
    );
}

export default AlbumManager;
